import { useState } from 'react';
import { todayIn } from '@njpos/core';
import { api, usd } from '../lib/api';
import { ErrorNote, Modal } from '../components/ui';
import AcctRegister from './AcctRegister';

const money = (v: string) => v.replace(/[^\d.]/g, '');

// Business credit cards: what you owe on each, what you charged, and paying them from a bank account.
export default function AcctCards({ cards, banks, reload, onAddExpense }: { cards: any[]; banks: any[]; reload: () => void; onAddExpense: () => void }) {
  const [edit, setEdit] = useState<any>(null);
  const [pay, setPay] = useState<any>(null);
  const [tick, setTick] = useState(0);
  const active = cards.filter((c) => c.active);
  return (
    <div className="stack">
      <div className="row wrap" style={{ gap: 8 }}>
        <button className="primary" onClick={() => setEdit({})}>Add a credit card</button>
        {active.length > 0 && <button onClick={onAddExpense}>Record a charge</button>}
      </div>
      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
        {active.map((c) => (
          <div key={c.id} className="panel"><div className="panel-body stack" style={{ gap: 6 }}>
            <div className="row"><h2 className="grow" style={{ margin: 0 }}>{c.name}</h2>{c.last4 && <span className="faint">···· {c.last4}</span>}</div>
            {c.issuer && <div className="faint">{c.issuer}</div>}
            <div><div className="num" style={{ fontSize: 28, fontWeight: 700 }}>{usd(c.owed_cents)}</div><div className="faint">You owe{c.credit_limit_cents ? ` · limit ${usd(c.credit_limit_cents)}` : ''}</div></div>
            <div className="row wrap" style={{ gap: 6 }}>
              <button className="primary" disabled={banks.length === 0} onClick={() => setPay(c)}>Pay this card</button>
              <button onClick={() => setEdit(c)}>Edit</button>
            </div>
          </div></div>))}
      </div>
      {active.length === 0 && <div className="panel"><div className="empty">No credit cards yet. Add one, then record what you charge on it under Expenses.</div></div>}
      {active.length > 0 && (<><h3>Charges and payments</h3><AcctRegister picks={active.map((c) => ({ code: c.gl_code, label: c.name }))} refreshKey={tick} /></>)}
      {edit && <CardForm c={edit} onClose={() => setEdit(null)} onDone={() => { setEdit(null); reload(); }} />}
      {pay && <PayCard c={pay} banks={banks} onClose={() => setPay(null)} onDone={() => { setPay(null); reload(); setTick((t) => t + 1); }} />}
    </div>
  );
}

function CardForm({ c, onClose, onDone }: { c: any; onClose: () => void; onDone: () => void }) {
  const [f, setF] = useState({ name: c.name ?? '', issuer: c.issuer ?? '', last4: c.last4 ?? '', limit: c.credit_limit_cents ? String(c.credit_limit_cents / 100) : '' });
  const [err, setErr] = useState<unknown>(null);
  return (
    <Modal title={c.id ? 'Edit credit card' : 'Add a credit card'} onClose={onClose} footer={<>
      {c.id && <button onClick={async () => { try { await api(`/api/accounting/cards/${c.id}`, { method: 'PUT', body: { name: f.name, issuer: f.issuer || null, last4: f.last4 || null, creditLimitCents: f.limit ? Math.round(Number(f.limit) * 100) : null, active: false } }); onDone(); } catch (e) { setErr(e); } }}>Stop using this card</button>}
      <button className="primary" disabled={!f.name.trim()} onClick={async () => {
        try {
          const body = { name: f.name, issuer: f.issuer || null, last4: f.last4 || null, creditLimitCents: f.limit ? Math.round(Number(f.limit) * 100) : null };
          if (c.id) await api(`/api/accounting/cards/${c.id}`, { method: 'PUT', body }); else await api('/api/accounting/cards', { body });
          onDone();
        } catch (e) { setErr(e); }
      }}>Save</button></>}>
      <ErrorNote error={err} />
      <div className="field-row"><label>Name<input autoFocus value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Business Visa" /></label><label>Bank or issuer<input value={f.issuer} onChange={(e) => setF({ ...f, issuer: e.target.value })} placeholder="Chase, Amex…" /></label></div>
      <div className="field-row"><label>Last 4 digits<input inputMode="numeric" maxLength={4} value={f.last4} onChange={(e) => setF({ ...f, last4: e.target.value.replace(/\D/g, '') })} /></label><label>Credit limit ($, optional)<input className="num-input" inputMode="decimal" value={f.limit} onChange={(e) => setF({ ...f, limit: money(e.target.value) })} /></label></div>
      <p className="faint">Never type the full card number here. The POS only needs the last four digits to tell your cards apart.</p>
    </Modal>
  );
}

function PayCard({ c, banks, onClose, onDone }: { c: any; banks: any[]; onClose: () => void; onDone: () => void }) {
  const [f, setF] = useState({ bank: banks[0]?.id ?? '', date: todayIn(), amount: c.owed_cents > 0 ? (c.owed_cents / 100).toFixed(2) : '' });
  const [err, setErr] = useState<unknown>(null);
  const cents = Math.round(Number(f.amount || '0') * 100);
  return (
    <Modal title={`Pay ${c.name}`} onClose={onClose} footer={<button className="primary" disabled={cents <= 0 || !f.bank} onClick={async () => {
      try { await api(`/api/accounting/cards/${c.id}/payments`, { body: { bankAccountId: f.bank, date: f.date, amountCents: cents } }); onDone(); } catch (e) { setErr(e); }
    }}>Record payment</button>}>
      <ErrorNote error={err} />
      <div className="field-row">
        <label>From<select value={f.bank} onChange={(e) => setF({ ...f, bank: e.target.value })}>{banks.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</select></label>
        <label>Date<input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></label>
        <label>Amount ($)<input className="num-input" inputMode="decimal" value={f.amount} onChange={(e) => setF({ ...f, amount: money(e.target.value) })} /></label>
      </div>
      <p className="faint">You owe {usd(c.owed_cents)} on this card. This records the payment in your books; pay the card itself on the card company's website.</p>
    </Modal>
  );
}
